import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { environment } from '../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { IHandledError } from './error.models';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly injector: Injector = inject(Injector);
  private static readonly CHUNK_RELOAD_KEY: string = 'chunkLoadErrorReloaded';
  showFullError: boolean = false;

  public static handleError(err: IHandledError): Observable<never> {
    if (err.error instanceof ErrorEvent) {
      const message: string = err.error.message;
      return throwError(() => `An error occurred: ${message}`);
    }
    return throwError(() => err.status);
  }

  handleError(error: IHandledError): void {
    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError();
      return;
    }

    const message: string = error.message ? error.message : error.toString();
    const errorToDisplay: string = `error: ${message}`;
    if (!environment.production && environment.showJsErrors) {
      const configFacade: ConfigurationFacade = this.injector.get(ConfigurationFacade);
      configFacade.setAlert({
        type: 'error',
        message: this.showFullError ? errorToDisplay : 'Something went wrong in JS! Please check the console.',
      });
      console.error('From Error Handler\n', error);
    } else {
      console.warn('Something went wrong', errorToDisplay);
      console.error(error);
    }
  }

  /**
   * Detects ChunkLoadError thrown when a lazy-loaded module's chunk file
   * is no longer available on the server (e.g. after a new deployment).
   */
  private isChunkLoadError(error: IHandledError): boolean {
    const chunkFailedMessage: RegExp = /Loading chunk [\S]+ failed/;
    return (
      error?.name === 'ChunkLoadError' ||
      (!!error?.message && chunkFailedMessage.test(error.message)) ||
      (!!error?.rejection && (error.rejection.name === 'ChunkLoadError' || chunkFailedMessage.test(error.rejection.message)))
    );
  }

  /**
   * Handles stale chunk references by reloading the page once.
   * Uses sessionStorage to prevent infinite reload loops.
   */
  private handleChunkLoadError(): void {
    const alreadyReloaded: string | null = sessionStorage.getItem(GlobalErrorHandler.CHUNK_RELOAD_KEY);
    if (!alreadyReloaded) {
      sessionStorage.setItem(GlobalErrorHandler.CHUNK_RELOAD_KEY, 'true');
      console.warn('ChunkLoadError detected — reloading to fetch updated application chunks.');
      this.reloadPage();
    } else {
      sessionStorage.removeItem(GlobalErrorHandler.CHUNK_RELOAD_KEY);
      console.error('ChunkLoadError persists after reload. The chunk may be genuinely missing.');
    }
  }

  /** @internal Extracted for testability — window.location.reload is not writable in Karma. */
  protected reloadPage(): void {
    window.location.reload();
  }
}
